// Chestionar de clarificare post-OCR. Replaces the simpler Q1/Q2/Q3 flow.
// Structured into 4 sections matching the spec the user provided 2026-05-17:
//   1. PROCESUL-VERBAL — date despre actul în sine (acoperă mismatchuri OCR)
//   2. CONTRAVENIENT — date personale + comunicare PV
//   3. ÎMPREJURĂRI — martori + punct de vedere
//   4. SITUAȚII — categorizare tip sancțiune + cauză
//
// Each section has Fields of various types. Some fields can pre-fill from the
// OCR-extracted PVExtracted; user confirms/corrects/fills gap.

import type { PVExtracted } from './pv-extracted.js';

export type FieldType = 'radio' | 'multi_select' | 'text' | 'textarea' | 'boolean_radio';

export interface FieldOption {
  id: string;
  label: string;
  /** Short tag the LLM uses to build the legal narrative — never shown to user. */
  context?: string;
  /** "hot" options visually pop and are weighted higher in defense angle generation. */
  hot?: boolean;
}

export interface Field {
  id: string;
  type: FieldType;
  question: string;
  hint?: string;
  options?: FieldOption[];
  required?: boolean;
  /** Dot-path into PVExtracted, e.g. "fapta.tip" or "agent_constatator.unitate". */
  prefillFrom?: string;
  /** If set, field only shows when other field's value matches. */
  showWhen?: { fieldId: string; equals: string };
  /** Placeholder for text/textarea fields. */
  placeholder?: string;
}

export interface Section {
  id: string;
  title: string;
  subtitle?: string;
  fields: Field[];
}

// ============================================================
//  Section 1: PROCESUL-VERBAL
// ============================================================

const SECTION_PV: Section = {
  id: 'sec_pv',
  title: 'Procesul-verbal',
  subtitle: 'Confirmă sau completează datele extrase din PV.',
  fields: [
    {
      id: 'institutie_agent',
      type: 'radio',
      question: 'Din ce instituție face parte agentul constatator?',
      required: true,
      options: [
        { id: 'igpr', label: 'Poliția Română — I.G.P.R.', context: 'agent I.G.P.R.' },
        { id: 'ipj', label: 'Poliția Română — I.P.J. (județean)', context: 'agent I.P.J.' },
        { id: 'politia_locala', label: 'Poliția Locală', context: 'agent Poliția Locală — competență teritorială restrânsă' },
        { id: 'politia_frontiera', label: 'Poliția de Frontieră', context: 'agent Poliția de Frontieră' },
        { id: 'altele_institutie', label: 'Altă instituție / nu sunt sigur', context: 'instituție incertă — verifică competența materială' },
      ],
    },
    {
      id: 'ora_data_intocmire',
      type: 'text',
      question: 'Ora, data și locul întocmirii PV',
      hint: 'Pre-completat de OCR. Corectează dacă e greșit. Ex: "12.05.2026, ora 14:23, Otopeni"',
      prefillFrom: 'data_emitere',
      required: true,
      placeholder: 'data, ora și locul întocmirii',
    },
    {
      id: 'ora_data_savarsire',
      type: 'text',
      question: 'Ora, data și locul săvârșirii contravenției',
      hint: 'Diferă uneori de momentul întocmirii PV (ex: PV făcut în secție după observație în trafic). Important pentru termen.',
      required: true,
      placeholder: 'data, ora și locul real al faptei',
    },
    {
      id: 'incadrare_articol_incalcat',
      type: 'text',
      question: 'Articolul de lege ÎNCĂLCAT (din PV)',
      hint: 'Ex: "art. 49 OUG 195/2002" — articolul pe care agentul susține că l-ai încălcat.',
      prefillFrom: 'incadrare_juridica.articol',
      placeholder: 'art. X din act normativ',
    },
    {
      id: 'incadrare_articol_sanctiune',
      type: 'text',
      question: 'Articolul de lege care SANCȚIONEAZĂ (din PV)',
      hint: 'Adesea diferit. Ex: "art. 100 OUG 195/2002 alin. 3 lit. b".',
      placeholder: 'art. care prevede sancțiunea',
    },
    {
      id: 'sanctiune_principala',
      type: 'radio',
      question: 'Ce sancțiune principală a fost aplicată?',
      required: true,
      options: [
        { id: 'avertisment', label: 'Avertisment (verbal sau scris)', context: 'sancțiune principală: avertisment' },
        { id: 'amenda', label: 'Amendă', context: 'sancțiune principală: amendă' },
      ],
    },
    {
      id: 'sanctiuni_complementare',
      type: 'multi_select',
      question: 'Sancțiuni complementare aplicate (bifează tot ce e cazul)',
      options: [
        { id: 'puncte_penalizare', label: 'Puncte de penalizare', context: 'puncte de penalizare aplicate' },
        { id: 'suspendare_permis', label: 'Suspendarea permisului de conducere pe timp limitat', context: 'suspendare permis — efect suspensiv al plângerii (art. 118 OUG 195/2002)', hot: true },
        { id: 'confiscare', label: 'Confiscarea bunurilor', context: 'confiscare bunuri — verifică temei legal expres' },
        { id: 'fara_complementare', label: 'Niciuna', context: 'fără sancțiuni complementare' },
      ],
    },
    {
      id: 'masuri_tehnico_administrative',
      type: 'multi_select',
      question: 'Măsuri tehnico-administrative dispuse',
      options: [
        { id: 'retinere_permis', label: 'Reținerea permisului / certificatului / dovezii înlocuitoare', context: 'reținere permis — efect imediat, dar plângerea suspendă executarea' },
        { id: 'retragere_documente', label: 'Retragerea permisului / certificat / plăcuțe', context: 'retragere documente — măsură severă' },
        { id: 'anulare_permis', label: 'Anularea permisului', context: 'anulare permis — verifică legalitatea procedurii' },
        { id: 'ridicare_vehicul_nereg', label: 'Ridicarea vehiculului staționat neregulamentar', context: 'ridicare vehicul neregulamentar — verifică marcaje' },
        { id: 'interdictie_conducere', label: 'Interdicția de a conduce în România (permis străin)', context: 'interdicție conducere — permis străin' },
        { id: 'relocare_vehicul', label: 'Relocarea vehiculului staționat regulamentar', context: 'relocare vehicul regulamentar — verifică justificare', hot: true },
        { id: 'imobilizare', label: 'Imobilizarea vehiculului', context: 'imobilizare vehicul — măsură procedurală' },
        { id: 'fara_tehnico', label: 'Niciuna', context: 'fără măsuri tehnico-administrative' },
      ],
    },
    {
      id: 'mod_sesizare',
      type: 'radio',
      question: 'Cum a aflat agentul de fapta ta?',
      required: true,
      options: [
        { id: 'direct', label: 'Direct, m-a oprit personal în trafic', context: 'sesizare directă — agent prezent la fapta' },
        { id: 'eveniment_rutier', label: 'Ca urmare a unui eveniment rutier (accident)', context: 'sesizare prin accident rutier — verifică art. 110 (probe biologice) dacă e cazul' },
        { id: 'inregistrari_terte', label: 'Înregistrări de la terțe persoane', context: 'sesizare prin terț — verifică valabilitatea înregistrării', hot: true },
        { id: 'sisteme_supraveghere', label: 'Sisteme de supraveghere omologate (camere, radar fix)', context: 'sesizare prin sistem automat — verifică omologare metrologie (art. 49 HG 1391/2006)' },
      ],
    },
  ],
};

// ============================================================
//  Section 2: CONTRAVENIENT
// ============================================================

const SECTION_CONTRAVENIENT: Section = {
  id: 'sec_contravenient',
  title: 'Despre tine',
  subtitle: 'Date personale și modul în care ai luat la cunoștință de PV.',
  fields: [
    {
      id: 'varsta',
      type: 'radio',
      question: 'Vârsta ta la data faptei',
      required: true,
      options: [
        { id: 'minor', label: 'Între 14 și 18 ani', context: 'minor — sancțiunea se aplică cu particularități (art. 11 OG 2/2001)', hot: true },
        { id: 'major', label: 'Peste 18 ani', context: 'major' },
      ],
    },
    {
      id: 'comunicare_pv',
      type: 'radio',
      question: 'Cum ai luat la cunoștință de PV?',
      required: true,
      hint: 'Asta determină de când curge termenul de 15 zile pentru plângere (art. 33 OG 2/2001).',
      options: [
        { id: 'semnatura', label: 'Pe loc, prin semnătură', context: 'luare la cunoștință cu semnătură — termen 15 zile curge din acel moment' },
        { id: 'refuz', label: 'Am refuzat să iau la cunoștință; PV comunicat ulterior', context: 'refuz luare la cunoștință — necesar martor asistent (art. 19 OG 2/2001)', hot: true },
        { id: 'absent', label: 'Nu eram prezent; PV comunicat ulterior', context: 'absent la constatare — obligatoriu martor asistent (art. 19 OG 2/2001)', hot: true },
        { id: 'instiintare_plata', label: 'Doar printr-o înștiințare de plată de la altă instituție', context: 'comunicare doar prin terț (ex: ANAF) — invocă lipsă comunicare regulată (art. 21 OG 2/2001)', hot: true },
      ],
    },
    {
      id: 'punct_vedere_consemnat',
      type: 'radio',
      question: 'Ți-a fost permis să consemnezi un punct de vedere în PV?',
      required: true,
      hint: 'Conform art. 16 alin. 1 lit. h OG 2/2001, ai drept la consemnare obiecțiuni.',
      options: [
        { id: 'nu_consemnat_voluntar', label: 'Nu am consemnat (nu am vrut)', context: 'fără obiecțiuni consemnate, voluntar' },
        { id: 'nu_informat', label: 'Nu mi s-a adus la cunoștință acest drept', context: 'încălcare procedurală — nu informat despre dreptul de obiecțiuni (art. 142 HG 1391/2006)', hot: true },
        { id: 'nu_permis', label: 'Nu mi s-a permis să consemnez', context: 'încălcare gravă — refuz consemnare obiecțiuni; martori posibili la fața locului', hot: true },
        { id: 'consemnat', label: 'Da, am consemnat', context: 'obiecțiuni consemnate în PV — verifică dacă sunt incluse în extrasul OCR' },
      ],
    },
    {
      id: 'obiectiuni_text',
      type: 'textarea',
      question: 'Ce ai consemnat la obiecțiuni? (dacă da)',
      hint: 'Textul exact, cât de aproape îți amintești.',
      showWhen: { fieldId: 'punct_vedere_consemnat', equals: 'consemnat' },
      placeholder: 'Ex: "Nu am fost de acord cu valoarea măsurată, am cerut buletinul metrologic"',
    },
  ],
};

// ============================================================
//  Section 3: ÎMPREJURĂRI
// ============================================================

const SECTION_IMPREJURARI: Section = {
  id: 'sec_imprejurari',
  title: 'Împrejurări',
  subtitle: 'Martori și punctul tău de vedere despre faptă.',
  fields: [
    {
      id: 'martori_prezenti',
      type: 'radio',
      question: 'Au existat martori la momentul faptei?',
      required: true,
      options: [
        { id: 'martori_da', label: 'Da, au existat martori', context: 'martori existenți — pot fi citați în plângere', hot: true },
        { id: 'martori_nu', label: 'Nu au existat martori', context: 'fără martori — verificare martor asistent obligatorie (art. 19 OG 2/2001)' },
        { id: 'martori_nesigur', label: 'Nu sunt sigur', context: 'martori incerți' },
      ],
    },
    {
      id: 'martori_disponibili',
      type: 'radio',
      question: 'Pot martorii fi solicitați pentru proceduri ulterioare?',
      hint: 'Citarea martorilor în plângere e o probă puternică.',
      showWhen: { fieldId: 'martori_prezenti', equals: 'martori_da' },
      options: [
        { id: 'disponibili_da', label: 'Da, mi-ar putea da contactul', context: 'martori disponibili pentru citare — folosește în plângere', hot: true },
        { id: 'disponibili_nu', label: 'Nu / nu-i pot contacta', context: 'martori indisponibili' },
      ],
    },
    {
      id: 'punct_vedere_fapta',
      type: 'multi_select',
      question: 'Care este punctul tău de vedere asupra faptei? (bifează tot ce e cazul)',
      required: true,
      options: [
        { id: 'nu_am_savarsit', label: 'NU eu am săvârșit fapta (identificare greșită, altă persoană)', context: 'identificare greșită — negare totală, cere probe agent', hot: true },
        { id: 'fara_intentie', label: 'Nu am avut intenția de a săvârși fapta', context: 'lipsă intenție — invocă vinovăție lipsă (art. 1 OG 2/2001)', hot: true },
        { id: 'forta_majora', label: 'A fost caz de forță majoră (urgență medicală, defecțiune, etc.)', context: 'forță majoră / stare de necesitate — art. 11 OG 2/2001 exonerează răspunderea', hot: true },
        { id: 'fara_semnal', label: 'Nu am sesizat semnalul rutier de atenționare (semn lipsă, șters, acoperit)', context: 'semnalizare deficitară — fapta nu este săvârșită cu vinovăție', hot: true },
        { id: 'recunosc', label: 'Recunosc fapta dar contest sancțiunea / procedura', context: 'recunoaștere faptă; atac pe formă sau cuantum' },
      ],
    },
    {
      id: 'forta_majora_detalii',
      type: 'textarea',
      question: 'Detalii despre forța majoră / urgență',
      showWhen: { fieldId: 'punct_vedere_fapta', equals: 'forta_majora' },
      placeholder: 'Ex: "Soția în chinurile nașterii, dovadă fișa de internare"',
    },
  ],
};

// ============================================================
//  Section 4: SITUAȚII (categorizare)
// ============================================================

const SECTION_SITUATII: Section = {
  id: 'sec_situatii',
  title: 'Tipul sancțiunii și cauza',
  subtitle: 'Categorizarea finală — ne ajută să alegem strategia.',
  fields: [
    {
      id: 'tip_sanctiune_compusa',
      type: 'radio',
      question: 'Cum se compune sancțiunea aplicată?',
      required: true,
      options: [
        { id: 'tip_1', label: 'Amendă + puncte penalizare', context: 'compoziție 1: amendă + puncte penalizare' },
        { id: 'tip_2', label: 'Amendă + puncte penalizare + sancțiuni complementare', context: 'compoziție 2: + complementare (verifică art. 5 OG 2/2001)' },
        { id: 'tip_3', label: 'Amendă + puncte penalizare + măsuri tehnico-administrative', context: 'compoziție 3: + tehnico-administrative' },
        { id: 'tip_4', label: 'Amendă + puncte + complementare + tehnico-administrative', context: 'compoziție 4: sancțiune maximă cumulată — verifică cumulul legal' },
      ],
    },
    {
      id: 'cauza_sanctiune',
      type: 'radio',
      question: 'Care a fost cauza concretă a sancționării?',
      required: true,
      options: [
        { id: 'cauza_accident', label: 'Ca urmare a unui accident rutier', context: 'cauză: accident rutier — verifică art. 110 (alcoolemie), proba biologică' },
        { id: 'cauza_circulatie', label: 'Încălcare a regulilor de circulație în mod direct', context: 'cauză: încălcare regulă circulație' },
        { id: 'cauza_neconformare', label: 'Refuz de legitimare / neconformare la solicitare', context: 'cauză: refuz legitimare — verifică temei legal solicitare', hot: true },
        { id: 'cauza_sesizare_tert', label: 'Sesizare de la terțe persoane (înregistrare amator, vecini)', context: 'cauză: sesizare terț — verifică valabilitatea probei terților', hot: true },
        { id: 'cauza_neindeplinire', label: 'Neîndeplinire obligație (ITP, asigurare, RCA expirat)', context: 'cauză: neîndeplinire obligație administrativă' },
      ],
    },
    {
      id: 'note_libere',
      type: 'textarea',
      question: 'Orice altceva ce ar trebui să știm?',
      hint: 'Detalii libere — circumstanțe, contradicții cu agentul, dorințe specifice pentru plângere. Opțional.',
      placeholder: 'Ex: agentul a fost agresiv, am cerut buletin metrologic și a refuzat, am martori în mașină...',
    },
  ],
};

// ============================================================
//  Selector
// ============================================================

export function getChestionar(): Section[] {
  return [SECTION_PV, SECTION_CONTRAVENIENT, SECTION_IMPREJURARI, SECTION_SITUATII];
}

/** Read a dot-path from a PVExtracted object, returns the first found string. */
export function readExtractedPath(extracted: PVExtracted | null, path: string): string | null {
  if (!extracted) return null;
  const parts = path.split('.');
  let cur: unknown = extracted;
  for (const p of parts) {
    if (cur == null || typeof cur !== 'object') return null;
    cur = (cur as Record<string, unknown>)[p];
  }
  if (cur == null) return null;
  if (typeof cur === 'string') return cur;
  if (typeof cur === 'number') return String(cur);
  return null;
}
